import { memo, useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import "./css/NavBar.css";
import Dropdown from "./common/Dropdown";
import { Badge, Dropdown as BootstrapDropdown } from "react-bootstrap";
import { FaBell, FaEnvelope } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useGetUserNotification } from "../api/queries/useGetUserNotification";
import { useAppContext } from "../Context/AppContext";
import { clearTokens, isLoggedIn } from "../auth/authStorage";
import { clearAuthSensitiveQueries } from "../auth/authSession";
import { useMembershipSidebarGate } from "../hooks/useMembershipSidebarGate";
import LiveTranslationDropdown from "./common/LiveTranslationDropdown";
import { useTranslation } from "react-i18next";

interface NavBarProps {
  toggleSidebar: () => void;
}

interface NotificationButtonProps {
  onClick: () => void;
  count: number;
}

function NotificationButton({ onClick, count }: NotificationButtonProps) {
  return (
    <button
      onClick={onClick}
      className="position-relative cursor-pointer border-0 bg-transparent p-0"
    >
      <FaBell size={18} />
      {count > 0 && (
        <Badge
          bg="danger"
          pill
          className="position-absolute top-0 start-100 translate-middle"
          style={{ fontSize: "0.5rem" }}
        >
          {count}
        </Badge>
      )}
    </button>
  );
}

function Navbar({ toggleSidebar }: NavBarProps) {
  const { t } = useTranslation();
  const isAuthenticated = isLoggedIn();
  const { user } = useAppContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: notifications } = useGetUserNotification({
    id: user?.user?.id ?? 0,
  });

  const unreadNotifications = useMemo(
    () =>
      notifications?.data?.filter(
        (item: { isRead: boolean }) => item.isRead === false,
      ) || [],
    [notifications?.data],
  );

  const handleLogout = useCallback(async () => {
    clearAuthSensitiveQueries(queryClient);
    clearTokens();
    navigate("/", { replace: true });
  }, [navigate, queryClient]);

  const handleNavigateToMessages = useCallback(() => {
    navigate("/messages");
  }, [navigate]);

  const handleNavigateToNotifications = useCallback(() => {
    navigate("/notifications");
  }, [navigate]);

  const handleNavigateToAccount = useCallback(() => {
    navigate("/my-account");
  }, [navigate]);

  const userProfileImage = useMemo(
    () =>
      user?.imageUrls.PersonalProfessionalPhoto ||
      user?.imageUrls.ProfessionalPhoto ||
      "/user.png",
    [
      user?.imageUrls.PersonalProfessionalPhoto,
      user?.imageUrls.ProfessionalPhoto,
    ],
  );

  const hasPaid = user?.user?.hasPaid === 1;
  const { treatAsSidebarMember } = useMembershipSidebarGate();
  const unreadCount = unreadNotifications.length;
  const memberBenefits = useMemo(
    () => [
      { title: t("nav.businessMembership"), href: "business-membership" },
      { title: t("nav.executiveMembership"), href: "executive-membership" },
      { title: t("nav.earlyStageMembership"), href: "early-stage-membership" },
    ],
    [t],
  );
  const internationalTrades = useMemo(
    () => [
      {
        title: t("nav.executiveInternationalMembership"),
        href: "executive-international-membership",
      },
      {
        title: t("nav.saudiTradeProgramme"),
        href: "saudi-arabia-international-trade-programme",
      },
      {
        title: t("nav.franceDelegation"),
        href: "france-business-delegation-2025",
      },
      {
        title: t("nav.turkiyeTradeProgramme"),
        href: "smbf-turkiye-international-trade-programme",
      },
      {
        title: t("nav.dubaiDelegation"),
        href: "dubai-business-delegation",
      },
    ],
    [t],
  );

  if (!isAuthenticated) {
    return (
      <nav className="navbar navbar-expand-lg navbar-light fixed-top">
        <div className="container">
          <a className="navbar-brand" href="/">
            <img
              src="/connect-logo.png"
              className="logo"
              alt={t("nav.logoAlt")}
              width={125}
              height={80}
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
          </a>
          <button
            className="navbar-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarNav"
            aria-controls="navbarNav"
            aria-expanded="false"
            aria-label={t("nav.toggleNavigation")}
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav me-auto">
              <li className="nav-item">
                <a className="nav-link" href="/our-vision">
                  {t("nav.ourVision")}
                </a>
              </li>
              <li className="nav-item">
                <Dropdown
                  title={t("nav.memberBenefits")}
                  href="/members-benefits-smbf"
                  items={memberBenefits}
                />
              </li>
              <li className="nav-item">
                <a className="nav-link" href="/events">
                  {t("nav.events")}
                </a>
              </li>
              <li className="nav-item">
                <Dropdown
                  title={t("nav.internationalTrade")}
                  href="/international-business-set-up-programme"
                  items={internationalTrades}
                />
              </li>
              <li className="nav-item">
                <a className="nav-link" href="/support-our-growth">
                  {t("nav.supportUs")}
                </a>
              </li>
            </ul>
            <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center gap-2 ms-lg-auto py-2 py-lg-0">
              <LiveTranslationDropdown />
            </div>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav className="navbar navbar-expand-lg navbar-light fixed-top p-0">
      <div className="d-flex justify-content-between w-100 p-0">
        <div
          className={`d-flex flex-lg-row justify-content-between px-1 px-md-4 ${
            treatAsSidebarMember ? "nav-branding" : ""
          }`}
        >
          <a className="navbar-brand" href="/dashboard">
            <img
              src="/connect-logo.png"
              className="logo"
              alt={t("nav.logoAlt")}
              width={125}
              height={80}
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
          </a>
          {treatAsSidebarMember && (
            <div className="d-flex align-items-center gap-3">
              {hasPaid && (
                <div className="d-flex d-md-none align-items-center gap-3">
                  <button
                    onClick={handleNavigateToMessages}
                    className="position-relative cursor-pointer border-0 bg-transparent p-0"
                  >
                    <FaEnvelope size={18} />
                  </button>
                  <NotificationButton
                    onClick={handleNavigateToNotifications}
                    count={unreadCount}
                  />
                </div>
              )}
              <button
                className="navbar-toggler text-black d-block border-0 m-0 my-auto"
                type="button"
                onClick={toggleSidebar}
              >
                <span className="navbar-toggler-icon"></span>
              </button>
            </div>
          )}
        </div>

        <div className="d-flex align-items-center justify-content-end gap-4 w-100 px-4">
          <LiveTranslationDropdown />
          {hasPaid && (
            <>
              <button
                onClick={handleNavigateToMessages}
                className="position-relative cursor-pointer border-0 bg-transparent p-0"
              >
                <FaEnvelope size={18} />
              </button>
              <NotificationButton
                onClick={handleNavigateToNotifications}
                count={unreadCount}
              />
            </>
          )}

          <BootstrapDropdown align="end">
            <BootstrapDropdown.Toggle
              variant="link"
              className="rounded-circle p-0"
              id="dropdown-custom-components"
            >
              <img
                src={userProfileImage}
                alt={t("nav.userProfileAlt")}
                className="rounded-circle"
                width={40}
                height={40}
                loading="lazy"
                decoding="async"
                style={{ objectFit: "cover" }}
              />
            </BootstrapDropdown.Toggle>
            <BootstrapDropdown.Menu>
              <BootstrapDropdown.Item onClick={handleNavigateToAccount}>
                {t("nav.profile")}
              </BootstrapDropdown.Item>
              <BootstrapDropdown.Item onClick={handleLogout}>
                {t("nav.logout")}
              </BootstrapDropdown.Item>
            </BootstrapDropdown.Menu>
          </BootstrapDropdown>
        </div>
      </div>
    </nav>
  );
}

Navbar.displayName = "Navbar";

export default memo(Navbar);
